import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import WarehouseOrderService from '../services/WarehouseOrderService';

const initialState = {
  datas: [],
  details: null,
  pagination: { page: 0, per_page: 10, total: 0, totalPages: 0 },
  error: null,
  loading: false,
  submitLoading: false,
};

const handleError = (action) => action.payload?.data?.message || action.payload?.message || 'Something went wrong';

export const fetchWarehouseOrders = createAsyncThunk('warehouseOrder/all', async (params) => {
  try { return await WarehouseOrderService.All(params); } catch (e) { return e.response; }
});
export const createWarehouseOrder = createAsyncThunk('warehouseOrder/create', async (data) => {
  try { return await WarehouseOrderService.Create(data); } catch (e) { return e.response; }
});
export const fetchWarehouseOrderDetails = createAsyncThunk('warehouseOrder/details', async (id) => {
  try { return await WarehouseOrderService.Get(id); } catch (e) { return e.response; }
});
export const updateWarehouseOrder = createAsyncThunk('warehouseOrder/update', async ({ id, data }) => {
  try { return await WarehouseOrderService.Update(id, data); } catch (e) { return e.response; }
});
export const confirmWarehouseOrder = createAsyncThunk('warehouseOrder/confirm', async (id) => {
  try { return await WarehouseOrderService.Confirm(id); } catch (e) { return e.response; }
});
export const cancelWarehouseOrder = createAsyncThunk('warehouseOrder/cancel', async (id) => {
  try { return await WarehouseOrderService.Cancel(id); } catch (e) { return e.response; }
});
export const saveWarehouseOrderTransfer = createAsyncThunk('warehouseOrder/saveTransfer', async ({ id, data }) => {
  try { return await WarehouseOrderService.SaveTransfer(id, data); } catch (e) { return e.response; }
});
export const markWarehouseOrderTransferred = createAsyncThunk('warehouseOrder/markTransferred', async (id) => {
  try { return await WarehouseOrderService.MarkTransferred(id); } catch (e) { return e.response; }
});
export const cancelWarehouseOrderTransfer = createAsyncThunk('warehouseOrder/cancelTransfer', async (id) => {
  try { return await WarehouseOrderService.CancelTransfer(id); } catch (e) { return e.response; }
});
export const saveWarehouseOrderReceive = createAsyncThunk('warehouseOrder/saveReceive', async ({ id, data }) => {
  try { return await WarehouseOrderService.SaveReceive(id, data); } catch (e) { return e.response; }
});
export const confirmWarehouseOrderReceive = createAsyncThunk('warehouseOrder/confirmReceive', async (id) => {
  try { return await WarehouseOrderService.ConfirmReceive(id); } catch (e) { return e.response; }
});

const applyDetail = (state, action) => {
  state.submitLoading = false;
  if (action.payload?.status === 200) state.details = action.payload?.data?.data;
  else toast.error(handleError(action));
};

const slice = createSlice({
  name: 'warehouseOrder',
  initialState,
  reducers: {
    resetWarehouseOrderDetails: (state) => { state.details = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWarehouseOrders.pending, (state) => { state.loading = true; })
      .addCase(fetchWarehouseOrders.fulfilled, (state, action) => {
        if (action.payload?.status === 200) {
          state.datas = action.payload?.data?.data?.warehouseOrders || [];
          state.pagination = action.payload?.data?.data?.pagination || initialState.pagination;
        } else {
          toast.error(handleError(action));
        }
        state.loading = false;
      })
      .addCase(fetchWarehouseOrders.rejected, (state) => { state.loading = false; })
      .addCase(fetchWarehouseOrderDetails.pending, (state) => { state.loading = true; })
      .addCase(fetchWarehouseOrderDetails.fulfilled, (state, action) => {
        if (action.payload?.status === 200) state.details = action.payload?.data?.data;
        else toast.error(handleError(action));
        state.loading = false;
      })
      .addCase(createWarehouseOrder.pending, (state) => { state.submitLoading = true; })
      .addCase(createWarehouseOrder.fulfilled, (state) => { state.submitLoading = false; })
      .addCase(createWarehouseOrder.rejected, (state) => { state.submitLoading = false; })
      .addCase(updateWarehouseOrder.pending, (state) => { state.submitLoading = true; })
      .addCase(updateWarehouseOrder.fulfilled, (state) => { state.submitLoading = false; })
      .addCase(updateWarehouseOrder.rejected, (state) => { state.submitLoading = false; })
      .addCase(confirmWarehouseOrder.pending, (state) => { state.submitLoading = true; })
      .addCase(confirmWarehouseOrder.fulfilled, applyDetail)
      .addCase(confirmWarehouseOrder.rejected, (state) => { state.submitLoading = false; })
      .addCase(cancelWarehouseOrder.pending, (state) => { state.submitLoading = true; })
      .addCase(cancelWarehouseOrder.fulfilled, applyDetail)
      .addCase(cancelWarehouseOrder.rejected, (state) => { state.submitLoading = false; })
      .addCase(saveWarehouseOrderTransfer.pending, (state) => { state.submitLoading = true; })
      .addCase(saveWarehouseOrderTransfer.fulfilled, applyDetail)
      .addCase(saveWarehouseOrderTransfer.rejected, (state) => { state.submitLoading = false; })
      .addCase(markWarehouseOrderTransferred.pending, (state) => { state.submitLoading = true; })
      .addCase(markWarehouseOrderTransferred.fulfilled, applyDetail)
      .addCase(markWarehouseOrderTransferred.rejected, (state) => { state.submitLoading = false; })
      .addCase(cancelWarehouseOrderTransfer.pending, (state) => { state.submitLoading = true; })
      .addCase(cancelWarehouseOrderTransfer.fulfilled, applyDetail)
      .addCase(cancelWarehouseOrderTransfer.rejected, (state) => { state.submitLoading = false; })
      .addCase(saveWarehouseOrderReceive.pending, (state) => { state.submitLoading = true; })
      .addCase(saveWarehouseOrderReceive.fulfilled, applyDetail)
      .addCase(saveWarehouseOrderReceive.rejected, (state) => { state.submitLoading = false; })
      .addCase(confirmWarehouseOrderReceive.pending, (state) => { state.submitLoading = true; })
      .addCase(confirmWarehouseOrderReceive.fulfilled, applyDetail)
      .addCase(confirmWarehouseOrderReceive.rejected, (state) => { state.submitLoading = false; });
  },
});

export const { resetWarehouseOrderDetails } = slice.actions;
export const warehouseOrderSelector = (state) => state?.warehouseOrder;
export default slice.reducer;
